import {
  createFunction,
  updateFunction,
  deleteFunction
} from 'api/userFunctions';
import { userFunctionsKeys } from 'api/queryKeys';
import { useAreasQuery } from 'hooks/queries/useAreasQuery';
import { useMainCompanyQuery } from 'hooks/queries/useMainCompanyQuery';
import { useQueryClient } from '@tanstack/react-query';
import { Box, Flex, Modal } from 'components';
import { IGenericModal } from 'components/Modal/Modal';
import { P } from 'components/Typography/Typography';
import {
  expandSelectedAreaIdsWithAncestors,
  findOptionByValueInTree,
  flattenOptions
} from 'components/_form/NestedSelect/utilities';
import { NestedSelect } from 'components/_form/NestedSelect/NestedSelect';
import type { IOption as INestedSelectOption } from 'components/_form/NestedSelect/NestedSelect';
import { LabelPosition, Select, SwitchButton, TextField } from 'components/_form';
import { IOption } from 'components/_form/Select/Select';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import { IUserFunction } from 'types/functions';
import {
  parentToChildrenAreasStructure,
  parseParentToChildrenAreasStructureToOptions
} from 'utilities/areas';
import { companySupportedLanguageCodesExceptDefault } from 'utilities/companyLanguageCodes';

const STATE_OPTIONS: IOption[] = [
  { value: 'inactive', label: 'Inactive' },
  { value: 'active', label: 'Active' },
  { value: 'draft', label: 'Draft' }
];

interface IAddEditModal extends IGenericModal {
  initialFunction?: IUserFunction;
  onSuccess?: () => void;
}

const RowLabel = styled(P)`
  width: 30%;
  flex-shrink: 0;
`;
const InputCell = styled(Flex)`
  width: 70%;
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid ${({ theme }) => theme.palette.neutral.medium};
  margin: 12px 0;
  width: 100%;
`;

export const AddEditModal = ({
  isOpen,
  onCancelClick,
  initialFunction,
  onSuccess
}: IAddEditModal) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { data: mainCompany } = useMainCompanyQuery();
  const companyId = mainCompany?.id ?? '';
  const { data: areasData = [] } = useAreasQuery();

  const translationLocales = useMemo(
    () => companySupportedLanguageCodesExceptDefault(mainCompany ?? undefined),
    [
      mainCompany?.default_language_code,
      (mainCompany?.available_language_codes ?? []).join(',')
    ]
  );

  const [name, setName] = useState('');
  const [nameTranslations, setNameTranslations] = useState<Record<string, string>>({});
  const [state, setState] = useState<string>('active');
  const [areaIds, setAreaIds] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const areaOptions = useMemo(
    () =>
      parseParentToChildrenAreasStructureToOptions(
        parentToChildrenAreasStructure(areasData)
      ),
    [areasData]
  );

  useEffect(() => {
    if (!isOpen) return;
    const blankTranslations = () =>
      Object.fromEntries(translationLocales.map((loc) => [loc, ''])) as Record<string, string>;
    if (initialFunction) {
      setName(initialFunction.name);
      setState(initialFunction.state ?? 'active');
      setAreaIds((initialFunction.area_ids ?? []).map((id) => String(id)));
      setNameTranslations(
        translationLocales.length
          ? Object.fromEntries(
              translationLocales.map((loc) => [
                loc,
                initialFunction.name_translations?.[loc] ?? ''
              ])
            )
          : blankTranslations()
      );
    } else {
      setName('');
      setState('active');
      setAreaIds([]);
      setNameTranslations(blankTranslations());
    }
  }, [isOpen, initialFunction, translationLocales.join(',')]);

  const handleSave = useCallback(async () => {
    if (!name.trim()) {
      toast.error(t('common.fieldRequired'));
      return;
    }
    setIsSubmitting(true);
    try {
      const name_translations: Record<string, string> = {};
      translationLocales.forEach((loc) => {
        name_translations[loc] = (nameTranslations[loc] ?? '').trim();
      });
      const expandedAreaIds = expandSelectedAreaIdsWithAncestors(
        areaOptions,
        areaIds
          .map((id) => findOptionByValueInTree(areaOptions, id))
          .filter((o): o is INestedSelectOption => !!o)
      );
      const payload = {
        function: {
          name: name.trim(),
          state,
          area_ids: expandedAreaIds.map((id) => Number(id)),
          name_translations
        }
      };
      if (initialFunction) {
        await updateFunction(initialFunction.id, payload);
        toast.success(t('common.success'));
      } else {
        await createFunction(payload);
        toast.success(t('common.success'));
      }
      await queryClient.invalidateQueries({
        queryKey: userFunctionsKeys.list(companyId)
      });
      onSuccess?.();
    } catch (err) {
      toast.error(t('common.anErrorOccurred'));
    } finally {
      setIsSubmitting(false);
    }
  }, [
    name,
    nameTranslations,
    translationLocales,
    state,
    areaIds,
    areaOptions,
    initialFunction,
    companyId,
    queryClient,
    onSuccess,
    t
  ]);

  const handleDelete = useCallback(async () => {
    if (!initialFunction) return;
    if (!window.confirm(t('employeesAndUsersView.confirmDelete') ?? 'Delete this role?')) return;
    setIsSubmitting(true);
    try {
      await deleteFunction(initialFunction.id);
      toast.success(t('common.success'));
      await queryClient.invalidateQueries({
        queryKey: userFunctionsKeys.list(companyId)
      });
      onSuccess?.();
    } catch (err) {
      toast.error(t('common.anErrorOccurred'));
    } finally {
      setIsSubmitting(false);
    }
  }, [initialFunction, companyId, queryClient, onSuccess, t]);

  const isEditing = !!initialFunction;

  return (
    <Modal
      isOpen={isOpen}
      onCancelClick={onCancelClick}
      bodyMinHeight={['62vh', '68vh']}
      bodyMaxHeight={['85vh', 'min(92vh, 960px)']}
      headerTitle={
        isEditing
          ? t('employeesAndUsersView.editRole')
          : t('employeesAndUsersView.add')
      }
      mainButton={{
        label: t('buttons.save'),
        action: handleSave,
        isDisabled: isSubmitting,
        variant: 'eucalyptus'
      }}
      isSecondButtonVisible
      secondButton={{
        label: t('buttons.cancel'),
        action: onCancelClick,
        variant: 'grey'
      }}
      isThirdButtonVisible={isEditing}
      thirdButton={{
        label: t('buttons.delete'),
        action: handleDelete,
        isDisabled: isSubmitting
      }}
    >
      <Flex flexDirection="column" >
        <Flex flexDirection="row" alignItems="center" width="100%">
          <RowLabel variant="h4">{t('employeesAndUsersView.form.name')}</RowLabel>
          <InputCell>
            <TextField
              value={name}
              onChange={(e) => setName(e.target.value ?? '')}
              placeholder={t('employeesAndUsersView.form.name') ?? undefined}
            />
          </InputCell>
        </Flex>
        <Flex flexWrap="wrap" gap="12px" mt={2} width="100%">
          {translationLocales.map((locale) => (
            <Box key={locale} width={['100%', 'calc(50% - 6px)']}>
              <TextField
                value={nameTranslations[locale] ?? ''}
                onChange={(e) =>
                  setNameTranslations((prev) => ({
                    ...prev,
                    [locale]: e.target.value ?? ''
                  }))
                }
                label={`${locale.trim().toUpperCase()}:`}
                labelAsPlainText
                labelPosition={LabelPosition.INLINE}
              />
            </Box>
          ))}
        </Flex>
        <Divider />
        <Flex flexDirection="row" alignItems="center" width="100%">
          <Flex flexDirection="column" width="30%">
            <SwitchButton
              label={t('employeesAndUsersView.tableHeaders.active') + '?'}
              value={state === 'active'}
              onChange={(checked) => setState(checked ? 'active' : 'inactive')} />
          </Flex>
          <Flex flexDirection="column" width="70%">
            <NestedSelect
              options={areaOptions}
              selectedOptions={flattenOptions(areaOptions).filter((o) =>
                areaIds.includes(o.value ?? '')
              )}
              setSelectedOptions={(newOptions) =>
                setAreaIds(
                  flattenOptions(newOptions)
                    .map((o) => o.value || '')
                    .filter(Boolean)
                )
              }
              label={t('employeesAndUsersView.form.usingInAreas') as string}
              helperText={t('employeesAndUsersView.form.usingInAreasHelperText') as string}
            />
          </Flex>
        </Flex>
      </Flex>
    </Modal>
  );
};
